import { useEffect, useRef, useState } from 'react';
import { PlayCircle } from 'lucide-react';
import type { LearnVideo } from '../../services/learnService';
import { chapterIndexAt, formatClock } from './chapters';

const positionKey = (id: string) => `learn.video.${id}.t`;

function readPosition(id: string): number {
  try {
    const raw = localStorage.getItem(positionKey(id));
    const t = raw ? Number(raw) : 0;
    return Number.isFinite(t) && t > 0 ? t : 0;
  } catch {
    return 0;
  }
}

function savePosition(id: string, t: number) {
  try {
    localStorage.setItem(positionKey(id), String(Math.floor(t)));
  } catch {
    /* private mode or full storage: nothing to remember, nothing lost */
  }
}

/**
 * One training video: the player, then its chapters. Tapping a chapter jumps
 * there and plays; the chapter under the playhead is marked; the device
 * remembers where you stopped (Chris 10/31: the walkthrough under Learn).
 */
export function VideoLesson({ video }: { video: LearnVideo }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [time, setTime] = useState(0);
  const current = chapterIndexAt(video.chapters, time);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const restore = () => {
      const t = readPosition(video.id);
      if (t > 0 && t < el.duration - 5) el.currentTime = t;
    };
    el.addEventListener('loadedmetadata', restore);
    return () => el.removeEventListener('loadedmetadata', restore);
  }, [video.id]);

  const seek = (at: number) => {
    const el = ref.current;
    if (!el) return;
    el.currentTime = at;
    setTime(at);
    const p = el.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  };

  return (
    <section
      data-testid={`learn-video-${video.id}`}
      className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden"
    >
      <video
        ref={ref}
        data-testid="learn-video-player"
        controls
        playsInline
        preload="metadata"
        src={video.url}
        className="w-full aspect-video bg-black"
        onTimeUpdate={(e) => {
          const t = e.currentTarget.currentTime;
          setTime(t);
          if (Math.floor(t) % 5 === 0) savePosition(video.id, t);
        }}
        onPause={(e) => savePosition(video.id, e.currentTarget.currentTime)}
      />
      <div className="p-4 space-y-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">
            {video.title}
          </h2>
          <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
            {video.description}
          </p>
        </div>
        <ol
          aria-label={`Chapters of ${video.title}`}
          className="divide-y divide-slate-100 dark:divide-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-700"
        >
          {video.chapters.map((c, i) => (
            <li key={c.at}>
              <button
                type="button"
                onClick={() => seek(c.at)}
                aria-current={i === current ? 'true' : undefined}
                className={`w-full flex items-start gap-3 px-3 py-2.5 text-left text-sm transition-colors ${
                  i === current
                    ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                    : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                }`}
              >
                <PlayCircle className="w-4 h-4 mt-0.5 shrink-0 text-primary-500" />
                <span className="w-14 shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
                  {formatClock(c.at)}
                </span>
                <span>{c.label}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
